/**
 * src/services/eduPtmService.ts
 *
 * Client service for Parent-Teacher Meetings (PTM) (ED16.a2):
 * - Typed reads and writes through callable()
 * - Error mapping via mapEduPtmError and EDU_PTM_ERROR_CODE_MAP
 */

import { callable } from './callable.js'
import type { NlsKey } from '../nls/strings_nls.js'

export type EduPtmAudienceKind = 'ALL' | 'ROOT' | 'NODES'

export interface EduPtmAudience {
  kind: EduPtmAudienceKind
  rootId?: string | null
  nodeIds?: string[]
}

export type EduPtmDayStatus = 'ACTIVE' | 'CANCELLED'
export type EduPtmMeetingType = 'IN_PERSON' | 'ONLINE'
export type EduPtmBookingStatus = 'BOOKED' | 'CANCELLED' | 'MET' | 'NO_SHOW'

export interface EduPtmDayItem {
  id: string
  workspaceId: string
  module: 'teaching' | 'training'
  title: string
  description?: string
  date: string // YYYY-MM-DD
  audience: EduPtmAudience
  bookingOpenAt: string // ISO string
  bookingCloseAt: string // ISO string
  rescheduleCutoffAt: string // ISO string
  meetingType: EduPtmMeetingType
  location?: string
  timezone?: string
  cancelReason?: string | null
  status: EduPtmDayStatus
  createdAt: string
  updatedAt: string
}

export interface EduPtmWindowItem {
  startTime: string // HH:mm
  endTime: string // HH:mm
}

export interface EduPtmBreakItem {
  startTime: string // HH:mm
  endTime: string // HH:mm
  label?: string
}

export interface EduPtmTeacherWindowsItem {
  id: string
  dayId: string
  teacherUid: string
  teacherName?: string
  slotDurationMin: number
  windows: EduPtmWindowItem[]
  breaks: EduPtmBreakItem[]
  roomOrOnlineUrl?: string
  updatedAt?: string
}

export interface EduPtmSlotItem {
  slotStart: string // ISO UTC
  slotEnd: string // ISO UTC
  slotStartMs: number
  slotEndMs: number
  isBreak: boolean
  breakLabel?: string
  booking?: {
    id: string
    learnerId: string
    learnerName: string
    status: EduPtmBookingStatus
    bookedBy: string
    meetingId?: string | null
  } | null
}

export interface EduPtmBookingItem {
  id: string
  dayId: string
  teacherUid: string
  teacherName: string
  slotStart: string // ISO
  slotEnd: string // ISO
  slotStartMs: number
  slotEndMs: number
  learnerId: string
  learnerName: string
  bookedBy: string
  status: EduPtmBookingStatus
  meetingId?: string | null
  createdAt: string
  updatedAt: string
}

export interface EduPtmSummaryItem {
  id: string
  bookingId: string
  dayId: string
  teacherUid: string
  teacherName: string
  learnerId: string
  learnerName: string
  summary: string
  publishedAt: string
  updatedAt: string
}

// ---------------------------------------------------------------------------
// Error Mapping
// ---------------------------------------------------------------------------

export const EDU_PTM_ERROR_CODE_MAP: Record<string, NlsKey> = {
  ADMIN_ONLY: 'NLSEduPtmErrorAdminOnly',
  TEACHER_OR_ADMIN_ONLY: 'NLSEduPtmErrorTeacherOrAdminOnly',
  DAY_CANCELLED: 'NLSEduPtmErrorDayCancelled',
  BOOKING_NOT_OPEN: 'NLSEduPtmErrorBookingNotOpen',
  BOOKING_CLOSED: 'NLSEduPtmErrorBookingClosed',
  CUTOFF_PASSED: 'NLSEduPtmErrorCutoffPassed',
  SLOT_TAKEN: 'NLSEduPtmErrorSlotTaken',
  SLOT_IN_BREAK: 'NLSEduPtmErrorSlotInBreak',
  CHILD_ALREADY_BOOKED: 'NLSEduPtmErrorChildAlreadyBooked',
  GUARDIAN_CLASH: 'NLSEduPtmErrorGuardianClash',
  NOT_IN_AUDIENCE: 'NLSEduPtmErrorNotInAudience',
  MINOR_READ_ONLY: 'NLSEduPtmErrorMinorReadOnly',
  NOT_BOOKED_FAMILY: 'NLSEduPtmErrorNotBookedFamily',
  NOTE_STAFF_ONLY: 'NLSEduPtmErrorNoteStaffOnly',
  'permission-denied': 'NLSEduPtmErrorPermissionDenied',
}

export function mapEduPtmError(error: unknown): NlsKey {
  if (!error) return 'NLSEduPtmErrorGeneric'

  const errObj = typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : null
  const details = typeof errObj?.details === 'object' && errObj?.details !== null ? (errObj.details as Record<string, unknown>) : null

  const code =
    typeof errObj?.code === 'string'
      ? errObj.code
      : typeof details?.code === 'string'
        ? details.code
        : ''

  if (code && EDU_PTM_ERROR_CODE_MAP[code]) {
    return EDU_PTM_ERROR_CODE_MAP[code]
  }

  const message = typeof errObj?.message === 'string' ? errObj.message : String(error)

  if (EDU_PTM_ERROR_CODE_MAP[message]) {
    return EDU_PTM_ERROR_CODE_MAP[message]
  }

  const sortedEntries = Object.entries(EDU_PTM_ERROR_CODE_MAP).sort(
    (a, b) => b[0].length - a[0].length,
  )

  for (const [key, nlsKey] of sortedEntries) {
    if (message.includes(key)) {
      return nlsKey
    }
  }

  return 'NLSEduPtmErrorGeneric'
}

export function isSlotClashingWithOtherBookings(
  slot: { slotStart: string; slotEnd?: string; slotStartMs?: number; slotEndMs?: number; slotDurationMin?: number },
  otherBookings: Array<{ slotStartMs: number; slotEndMs: number }>,
): boolean {
  if (!slot?.slotStart || !otherBookings || otherBookings.length === 0) return false
  const startMs = slot.slotStartMs ?? new Date(slot.slotStart).getTime()
  const endMs = slot.slotEndMs ?? (slot.slotEnd ? new Date(slot.slotEnd).getTime() : startMs + (slot.slotDurationMin ?? 15) * 60000)
  return otherBookings.some((b) => startMs < b.slotEndMs && endMs > b.slotStartMs)
}

// ---------------------------------------------------------------------------
// Callable Wrappers
// ---------------------------------------------------------------------------

export async function createEduPtmDay(input: {
  workspaceId: string
  module?: 'teaching' | 'training'
  title: string
  description?: string
  date: string
  audience: EduPtmAudience
  bookingOpenAt: string
  bookingCloseAt: string
  rescheduleCutoffAt: string
  meetingType: EduPtmMeetingType
  location?: string
  requestId: string
}): Promise<{ day: EduPtmDayItem; created: boolean }> {
  return callable('createEduPtmDay', input)
}

export async function updateEduPtmDay(input: {
  workspaceId: string
  dayId: string
  title?: string
  description?: string
  date?: string
  audience?: EduPtmAudience
  bookingOpenAt?: string
  bookingCloseAt?: string
  rescheduleCutoffAt?: string
  meetingType?: EduPtmMeetingType
  location?: string
}): Promise<{ day: EduPtmDayItem }> {
  return callable('updateEduPtmDay', input)
}

export async function cancelEduPtmDay(input: {
  workspaceId: string
  dayId: string
  reason?: string
}): Promise<{ day: EduPtmDayItem }> {
  return callable('cancelEduPtmDay', input)
}

export async function listEduPtmDays(input: {
  workspaceId: string
  startDate?: string
  endDate?: string
  module?: 'teaching' | 'training'
}): Promise<{ days: EduPtmDayItem[] }> {
  return callable('listEduPtmDays', input)
}

export async function getEduPtmDay(input: {
  workspaceId: string
  dayId: string
}): Promise<{ day: EduPtmDayItem }> {
  return callable('getEduPtmDay', input)
}

export async function setEduPtmTeacherWindows(input: {
  workspaceId: string
  dayId: string
  teacherUid: string
  teacherName?: string
  slotDurationMin: number
  windows: EduPtmWindowItem[]
  breaks?: EduPtmBreakItem[]
  roomOrOnlineUrl?: string
}): Promise<{ success: boolean; id: string }> {
  return callable('setEduPtmTeacherWindows', input)
}

export async function getEduPtmTeacherWindows(input: {
  workspaceId: string
  dayId: string
  teacherUid: string
}): Promise<{ windows: EduPtmTeacherWindowsItem | null }> {
  return callable('getEduPtmTeacherWindows', input)
}

export async function listEduPtmSlots(input: {
  workspaceId: string
  dayId: string
  teacherUid: string
}): Promise<{ slots: EduPtmSlotItem[] }> {
  return callable('listEduPtmSlots', input)
}

export async function listFamilyEduPtmDays(input: {
  workspaceId: string
  learnerId: string
}): Promise<{ days: EduPtmDayItem[] }> {
  return callable('listFamilyEduPtmDays', input)
}

export interface FamilyEduPtmTeacherItem {
  teacherUid: string
  teacherName: string
  slotDurationMin: number
  roomOrOnline: string
  isOnline?: boolean
  booking?: EduPtmBookingItem | null
}

export async function listFamilyEduPtmTeachers(input: {
  workspaceId: string
  learnerId: string
  dayId: string
}): Promise<{ teachers: FamilyEduPtmTeacherItem[] }> {
  return callable('listFamilyEduPtmTeachers', input)
}

export async function listFamilyEduPtmSlots(input: {
  workspaceId: string
  learnerId: string
  dayId: string
  teacherUid: string
}): Promise<{ slots: EduPtmSlotItem[]; hasBookingWithTeacher: boolean; existingBooking?: EduPtmBookingItem | null }> {
  return callable('listFamilyEduPtmSlots', input)
}

export async function bookEduPtmSlot(input: {
  workspaceId: string
  dayId: string
  teacherUid: string
  slotStart: string
  learnerId: string
  onBehalf?: boolean
  requestId: string
}): Promise<{ booking: EduPtmBookingItem; created: boolean }> {
  return callable('bookEduPtmSlot', input)
}

export async function rescheduleEduPtmSlot(input: {
  workspaceId: string
  bookingId: string
  newSlotStart: string
  requestId: string
}): Promise<{ booking: EduPtmBookingItem }> {
  return callable('rescheduleEduPtmSlot', input)
}

export async function cancelEduPtmBooking(input: {
  workspaceId: string
  bookingId: string
  reason?: string
}): Promise<{ booking: EduPtmBookingItem }> {
  return callable('cancelEduPtmBooking', input)
}

export async function setEduPtmSlotOutcome(input: {
  workspaceId: string
  bookingId: string
  outcome: 'MET' | 'NO_SHOW'
}): Promise<{ success: boolean; outcome: string }> {
  return callable('setEduPtmSlotOutcome', input)
}

export async function setEduPtmPrivateNotes(input: {
  workspaceId: string
  bookingId: string
  notes: string
}): Promise<{ success: boolean }> {
  return callable('setEduPtmPrivateNotes', input)
}

export async function getEduPtmPrivateNotes(input: {
  workspaceId: string
  bookingId: string
}): Promise<{ notes: string; updatedAt?: string }> {
  return callable('getEduPtmPrivateNotes', input)
}

export async function publishEduPtmSummary(input: {
  workspaceId: string
  bookingId: string
  summary: string
}): Promise<{ success: boolean; summaryId: string }> {
  return callable('publishEduPtmSummary', input)
}

export async function listFamilyEduPtmSummaries(input: {
  workspaceId: string
  learnerId: string
}): Promise<{ summaries: EduPtmSummaryItem[] }> {
  return callable('listFamilyEduPtmSummaries', input)
}

export async function getPtmJoinInfo(input: {
  workspaceId: string
  bookingId: string
}): Promise<{ meetingId: string; joinUrl: string }> {
  return callable('getEduPtmJoinInfo', input)
}

export async function joinEduPtmMeeting(input: {
  workspaceId: string
  bookingId: string
}): Promise<{ joinUrl: string }> {
  return callable('joinEduPtmMeeting', input)
}
